import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Target,
  Eye,
  Users,
  Award,
  Shield,
  Zap,
  Leaf,
  Map,
  Globe,
  Mountain,
  Building,
  TreePine,
  Code,
  Heart,
  CheckCircle,
} from "lucide-react";
import SecondaryNavbar from "../../components/common/SecondaryNavbar";
import SecondaryHero from "../../components/common/SecondaryHero";
import team1 from "../../Assets/Teams/sasmita.png";
import team2 from "../../Assets/Teams/tanmay.png";
import team3 from "../../Assets/Teams/jasobant.png";
import team4 from "../../Assets/Teams/mrutyunjay.png";
import team5 from "../../Assets/Teams/subham.png";
import team6 from "../../Assets/Teams/rakesh.png";
import team7 from "../../Assets/Teams/rajat.png";
import team8 from "../../Assets/Teams/rudra.png";
import team9 from "../../Assets/Teams/bijayashree1.png";
import team10 from "../../Assets/Teams/ajay.png";
import team11 from "../../Assets/Teams/mina.png";
import team12 from "../../Assets/Teams/abinash.png";
import team13 from "../../Assets/Teams/uday.png";
import team14 from "../../Assets/Teams/giri.png";
import team15 from "../../Assets/Teams/subh.png";
import team16 from "../../Assets/Teams/mano.png";
import team17 from "../../Assets/Teams/akash.png";
import team18 from "../../Assets/Teams/dhana.png";

const AboutPage = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  // Leadership Team
  const leadership = [
    {
      name: "Mr. Ajaya Kumar Mohapatra",
      role: "Managing Director",
      bio: "20+ years of experience in GIS technology and environmental consulting. PhD in Geospatial Sciences from Stanford University. Passionate about sustainable development and technological innovation.",
      image: team10,
    },
    {
      name: "Mr. Jasobanta Mohapatra",
      role: "Director",
      bio: "10+ years in project management and business operations. Specialized in environmental projects and regulatory compliance. MBA from Harvard Business School.",
      image: team3,
    },
    {
      name: "Mr. Dhaneswar Panda",
      role: "Business Development Manager",
      bio: "Expert in software architecture and GIS applications. Former lead developer at major tech companies. Masters in Computer Science from MIT with focus on spatial computing.",
      image: team18,
    },
  ];

  // Core Team Members
  const teamMembers = [
    {
      name: "Mr. Rakesh Mohapatra",
      role: "Manager",
      department: "Survey",
      image: team6,
    },
    {
      name: "Mr. Minaketan Sethy",
      role: "Manager",
      department: "FDP",
      image: team11,
    },
    {
      name: "Mrs. Sasmita Rout",
      role: "GIS Engineer",
      department: "GIS Services",
      image: team1,
    },
    {
      name: "Mrs. Bijayashree Mohapatra",
      role: " GIS Engineer",
      department: "GIS",
      image: team9,
    },
    {
      name: "Mr. Mrityunjaya Mohapatra",
      role: "Account Head",
      department: "Accounts",
      image: team4,
    },
    {
      name: "Mr. Subham Sahu",
      role: "FDP Co-ordinator",
      department: "FDP",
      image: team5,
    },
    {
      name: "Mr. Subhransu Sekhar Swain",
      role: "Development Team Lead",
      department: "Software",
      image: team15,
    },
    {
      name: "Mr. Manoranjan Behera",
      role: "Sr. Full-Stack Developer",
      department: "Software",
      image: team16,
    },
    {
      name: "Mr. Akash Pattanaik",
      role: "Sr. UI/UX Designer",
      department: "Software",
      image: team17,
    },
    {
      name: "Mr. Tanmay Kumar Dash",
      role: "Coordinator",
      department: "GIS, Auto-CAD",
      image: team2,
    },
    {
      name: "Mr. Rajat Kumar Rout",
      role: "Surveyor",
      department: "Survey",
      image: team7,
    },
    // {
    //   name: "Mr. Shushant Giri",
    //   role: "Surveyor",
    //   department: "Survey",
    //   image: team14,
    // },
    {
      name: "Mr. Rudra Pratap Majhi",
      role: "Surveyor",
      department: "Survey",
      image: team8,
    },
    {
      name: "Mr. Uday Ku Mohapatra",
      role: "Survey Assistant",
      department: "Survey",
      image: team13,
    },
    // {
    //   name: "Mr. Abinash Dash",
    //   role: "Survey Assistant",
    //   department: "Survey",
    //   image: team12,
    // },
  ];

  // Core Values
  const coreValues = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Integrity",
      description:
        "We maintain transparency and ethical business practices in every project, building trust through honest and principled conduct.",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Accuracy",
      description:
        "We deliver precise data, reliable assessments, and technically sound outputs with meticulous attention to detail.",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Innovation",
      description:
        "We utilize advanced GIS tools, remote sensing, drones, and digital platforms to provide cutting-edge solutions.",
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Sustainability",
      description:
        "We minimize environmental impact and support responsible development through environmentally conscious practices.",
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Reliability",
      description:
        "We ensure consistent performance and dependable service delivery across all projects and client engagements.",
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Excellence",
      description:
        "We continuously improve our services and exceed expectations through dedication to quality and professional growth.",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Teamwork",
      description:
        "We collaborate effectively, combining diverse expertise to successfully execute complex projects with unity and respect.",
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Client-Centric",
      description:
        "We prioritize client needs, providing tailored solutions and exceptional service to build lasting partnerships.",
    },
  ];

  // Industries We Serve
  const industries = [
    {
      icon: <TreePine className="w-6 h-6" />,
      name: "Forestry",
      count: "25+ Projects",
    },
    {
      icon: <Mountain className="w-6 h-6" />,
      name: "Mining",
      count: "18+ Projects",
    },
    {
      icon: <Leaf className="w-6 h-6" />,
      name: "Environmental",
      count: "32+ Projects",
    },
    {
      icon: <Code className="w-6 h-6" />,
      name: "IT & Technology",
      count: "45+ Projects",
    },
    {
      icon: <Building className="w-6 h-6" />,
      name: "Infrastructure",
      count: "28+ Projects",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      name: "Urban Planning",
      count: "22+ Projects",
    },
  ];

  // Certifications & Achievements
  const certifications = [
    {
      name: "ISO 9001:2015",
      description: "Quality Management System Certified",
    },
    {
      name: "ISO 14001:2015",
      description: "Environmental Management Certified",
    },
    {
      name: "Government Approved",
      description: "Recognized by Environmental Ministry",
    },
    { name: "Award 2023", description: "Best GIS Solutions Provider" },
    {
      name: "Innovation Award",
      description: "Technology Innovation in Mapping",
    },
    { name: "Client Excellence", description: "95% Client Satisfaction Rate" },
  ];

  // Company Timeline
  const timeline = [
    {
      year: "2008",
      event: "Company Founded",
      description: "Founded as Maptech India",
    },
    {
      year: "2011",
      event: "Team Expansion",
      description: "Team expanded with more technical experts",
    },
    {
      year: "2012",
      event: "Incorporation",
      description:
        "Incorporated as Maptech Solutions Pvt. Ltd. on 18 July 2012",
    },
    {
      year: "2012",
      event: "ORSAC Empanelment",
      description: "Empaneled under ORSAC on 18 July 2012",
    },
    {
      year: "2012",
      event: "First Major Project",
      description:
        "First DGPS Survey Project – Railway Siting at Sambalpur for Sham Matalicks, Bhubaneswar",
    },
    {
      year: "2013",
      event: "First Forest Clearance",
      description:
        "Completed first Forest Clearance in Parivesh Portal for Nuapada Rural Works Division",
    },
    {
      year: "2015",
      event: "Mining & Geology Empanelment",
      description: "Empaneled under Dept. of Mining & Geology, Jharkhand",
    },
    {
      year: "2015",
      event: "Industry Recognition",
      description: "Received industry recognition for service excellence",
    },
    {
      year: "2016",
      event: "ISO Certification",
      description: "Achieved ISO certification",
    },
    {
      year: "2024",
      event: "First Wildlife Clearance",
      description: "First Wildlife Clearance – MAHANADI Wildlife Division",
    },
    {
      year: "2025",
      event: "CORS Survey",
      description: "Conducted CORS Survey for Dalmia Bharat Limited",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const stats = [
    {
      icon: <Map className="w-7 h-7" />,
      value: "80+",
      label: "Projects",
      delay: 0.1,
    },
    {
      icon: <Users className="w-7 h-7" />,
      value: "25+",
      label: "Experts",
      delay: 0.2,
    },
    {
      icon: <Award className="w-7 h-7" />,
      value: "15+",
      label: "Industries",
      delay: 0.3,
    },
    {
      icon: <Globe className="w-7 h-7" />,
      value: "98%",
      label: "Success Rate",
      delay: 0.4,
    },
  ];

  return (
    <div className="overflow-hidden">
      <SecondaryNavbar />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <SecondaryHero
          pageName="About Maptech"
          pageHead1="Mapping"
          pageHead2="the"
          pageHead3=""
          pageHead4="Future"
          pageDesc="Pioneering GIS and IT solutions with precision, innovation, and sustainability at our core."
          bgImgUrl="https://store-images.s-microsoft.com/image/apps.4083.14191174512742283.25b58903-5b4d-47c0-959c-c79a5aabf46c.17e473bf-b171-4bc5-b00e-f1a6f54977e1"
        />

        {/* Company Overview */}
        <section ref={sectionRef} className="py-6 lg:py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={
                  isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }
                }
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                  Our <span className="text-brandBlue">Story</span>
                </h2>
                <div className="space-y-4 text-lg text-secondaryTextColor leading-relaxed">
                  <p>
                    <strong>Maptech Solutions Pvt. Ltd.</strong> has been a
                    trusted leader in geospatial, environmental, and
                    technological services for over 18 years, building a strong
                    reputation for accuracy, innovation, and sustainable
                    solutions.
                  </p>

                  <p>
                    We specialize in comprehensive GIS and DGPS Survey services
                    for mapping, spatial analysis, and decision-making, along
                    with expert Forest & Wildlife Clearance consulting that
                    ensures regulatory compliance through precise assessments
                    and documentation.
                  </p>

                  <p>
                    Our team excels in end-to-end clearance services—from field
                    surveys and impact assessments to PARIVESH portal
                    submissions—using advanced drone technology, satellite
                    imagery, and GIS analysis to support infrastructure,
                    transmission, and development projects nationwide.
                  </p>

                  <p>
                    Through proactive coordination with government authorities
                    and a commitment to environmental responsibility, we enable
                    timely project approvals while minimizing ecological impact,
                    making us a preferred partner for both public and private
                    sector organizations.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-brandBlue to-brandGreen rounded-3xl p-8 text-white relative overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>

                  <div className="relative grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={
                          isInView
                            ? { opacity: 1, y: 0 }
                            : { opacity: 0, y: 20 }
                        }
                        transition={{ duration: 0.6, delay: stat.delay }}
                        className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
                      >
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          {stat.icon}
                        </div>
                        <div className="text-2xl font-bold mb-2">
                          {stat.value}
                        </div>
                        <div className="text-white/90 text-sm">
                          {stat.label}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Vision, Mission & Core Values */}
        <section className="py-6 lg:py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-4xl mx-auto mb-16"
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                Our <span className="text-brandBlue">Purpose</span>
              </h2>
              <p className="text-lg text-secondaryTextColor">
                Guiding principles that define our commitment to excellence and
                sustainable development
              </p>
            </motion.div>

            {/* Vision & Mission */}
            <div className="grid lg:grid-cols-2 gap-10 mb-20">
              {/* Vision */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8 lg:p-10 shadow-lg border border-blue-100"
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mr-5">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-primaryTextColor font-lexend">
                    Our Vision
                  </h3>
                </div>
                <div className="text-secondaryTextColor leading-relaxed space-y-4">
                  <p>
                    Our vision is to establish Maptech Solutions as a premier
                    and trusted leader in geospatial, environmental, and
                    technological services, recognized for our commitment to
                    accuracy, innovation, and sustainable development.
                  </p>
                  <p>
                    We aspire to set new industry benchmarks through advanced
                    technology, reliable data solutions, and environmentally
                    responsible practices. By consistently delivering
                    high-quality services and adapting to emerging global
                    standards, we aim to empower organizations, support critical
                    infrastructure growth, and contribute to a future where
                    development and environmental protection go hand in hand.
                  </p>
                </div>
              </motion.div>

              {/* Mission */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-8 lg:p-10 shadow-lg border border-emerald-100"
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-green-600 rounded-2xl flex items-center justify-center mr-5">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-primaryTextColor font-lexend">
                    Our Mission
                  </h3>
                </div>
                <div className="text-secondaryTextColor leading-relaxed space-y-4">
                  <p>
                    Our mission is to provide high-precision GIS, survey, and
                    environmental clearance services using modern tools,
                    scientific methodologies, and strict adherence to regulatory
                    frameworks.
                  </p>
                  <p>
                    We are dedicated to supporting clients with comprehensive
                    and dependable solutions that ensure compliance, minimize
                    risks, and enhance project efficiency. Through continuous
                    improvement, advanced technology adoption, and a
                    client-centric approach, we strive to deliver timely
                    results, strengthen professional relationships, and promote
                    sustainable development across all sectors we serve.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Core Values Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mr-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-primaryTextColor font-lexend">
                  Our Core Values
                </h3>
              </div>
              <p className="text-lg text-secondaryTextColor max-w-2xl mx-auto">
                Our core values form the foundation of who we are and how we
                operate, guiding every decision and action we take.
              </p>
            </motion.div>

            {/* Core Values Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {coreValues.map((value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-brandBlue to-brandGreen rounded-xl flex items-center justify-center mx-auto mb-5 text-white group-hover:scale-110 transition-transform duration-300 group-hover:rotate-3">
                    {value.icon}
                  </div>
                  <h4 className="text-lg font-bold text-primaryTextColor mb-3 font-lexend text-center">
                    {value.title}
                  </h4>
                  <p className="text-secondaryTextColor text-sm leading-relaxed text-center">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Our Journey Timeline */}
        <section className="py-6 lg:py-12 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-10"
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                Our <span className="text-brandBlue">Journey</span>
              </h2>
              <p className="text-xl text-secondaryTextColor">
                From humble beginnings to industry leadership
              </p>
            </motion.div>

            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-brandBlue to-brandGreen"></div>

              {/* Timeline Items */}
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`relative flex items-center ${
                      index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                    }`}
                  >
                    {/* Content */}
                    <div
                      className={`w-1/2 ${
                        index % 2 === 0 ? "pr-8 text-right" : "pl-8"
                      }`}
                    >
                      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                        <div className="text-2xl font-bold text-brandBlue mb-2">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-bold text-primaryTextColor mb-2 font-lexend">
                          {item.event}
                        </h3>
                        <p className="text-secondaryTextColor">
                          {item.description}
                        </p>
                      </div>
                    </div>

                    {/* Timeline Dot */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-white border-4 border-brandBlue rounded-full z-10"></div>

                    {/* Spacer for the other side */}
                    <div className="w-1/2"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="py-6 lg:py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-10"
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                Visionary <span className="text-brandBlue">Leaders</span>
              </h2>
              <p className="text-xl text-secondaryTextColor">
                Meet the visionaries driving our success
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {leadership.map((leader, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-3xl border border-gray-200 hover:shadow-xl transition-all duration-500 overflow-hidden group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-primaryTextColor mb-2 font-lexend">
                      {leader.name}
                    </h3>
                    <p className="text-brandBlue font-semibold mb-4">
                      {leader.role}
                    </p>
                    <p className="text-secondaryTextColor text-sm leading-relaxed">
                      {leader.bio}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Team Members */}
        <section className="bg-grey-50 py-6 lg:py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                The Minds Behind the{" "}
                <span className="text-brandBlue">Mission</span>
              </h2>
              <p className="text-xl text-secondaryTextColor">
                Dedicated professionals driving innovation and excellence
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10"
            >
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-2xl border border-gray-200 p-6 text-center hover:shadow-lg transition-all duration-300 group flex flex-col"
                >
                  <div className="relative mb-5 flex-grow-0">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-24 h-24 rounded-full object-cover mx-auto group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-lg font-bold text-primaryTextColor mb-2 font-lexend">
                      {member.name}
                    </h3>
                    <p className="text-brandBlue font-semibold mb-2">
                      {member.role}
                    </p>
                    <p className="text-secondaryTextColor text-sm">
                      {member.department}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="pb-6 lg:pb-12 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-10"
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                Industries We <span className="text-brandBlue">Serve</span>
              </h2>
              <p className="text-xl text-secondaryTextColor">
                Expertise across multiple sectors and domains
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-brandBlue to-brandGreen rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                      {industry.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-primaryTextColor mb-1 font-lexend">
                        {industry.name}
                      </h3>
                      <p className="text-secondaryTextColor text-sm">
                        {industry.count}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Certifications & Achievements */}
        <section className="py-6 lg:py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-10"
            >
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-6 font-lexend">
                Certifications &{" "}
                <span className="text-brandBlue">Achievements</span>
              </h2>
              <p className="text-xl text-secondaryTextColor">
                Recognized for excellence and quality standards
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 group text-center"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-brandBlue to-brandGreen rounded-xl flex items-center justify-center text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Award className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-primaryTextColor mb-2 font-lexend">
                    {cert.name}
                  </h3>
                  <p className="text-secondaryTextColor text-sm">
                    {cert.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
